import React, { useState, useEffect } from 'react';
import { db, auth } from './firebase'; 
import { collection, onSnapshot, addDoc, deleteDoc, doc, updateDoc, serverTimestamp, query, orderBy, getDocs, getDoc } from 'firebase/firestore';
import { 
  FaRobot, FaPlus, FaTrash, FaClock, FaCalendarAlt, FaUsers, 
  FaCogs, FaCheckCircle, FaTimes, FaRocket, FaPlay, FaPause, 
  FaChartBar, FaBullhorn, FaEye, FaSearch, FaTag, FaBolt, FaArrowDown, FaChartPie, FaCheckDouble, FaCheck, FaBan, FaReply, FaStopCircle, FaSyncAlt, FaRedoAlt
} from 'react-icons/fa';

// --- ULTRA PRO ENTERPRISE THEME ---
const THEME = {
  primary: '#4f46e5',   // Indigo SaaS Primary
  primaryLight: '#e0e7ff',
  success: '#10b981', 
  warning: '#f59e0b',
  error: '#ef4444', 
  bg: '#f8fafc',      
  surface: '#ffffff', 
  textMain: '#0f172a',
  textSub: '#64748b',
  border: '#e2e8f0',
  accent: '#f1f5f9',
  waGreen: '#25D366'
};

// --- REAL-TIME TRACKING ROW FOR DRIPS ---
const DripTrackingRow = ({ contactId, campaignId, user, onRemove }) => {
  const [logData, setLogData] = useState(null);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, "businesses", user.uid, "drip_enrollments", `${campaignId}_${contactId}`), (docSnap) => {
      if (docSnap.exists()) {
        setLogData(docSnap.data());
      } else {
        setLogData({ status: 'un-enrolled' });
      }
    });
    return () => unsub();
  }, [contactId, campaignId, user]);

  if (!logData) return null;
  if (logData.status === 'un-enrolled') return null; 

  const getStatusBadge = () => {
    if (logData.replied) return <span style={{ background: '#dcfce7', color: '#15803d', padding: '6px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: '800', display:'inline-flex', alignItems:'center', gap:'5px' }}><FaReply size={12}/> Converted (Replied)</span>;
    if (logData.status === 'completed') return <span style={{ background: '#f1f5f9', color: '#475569', padding: '6px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: '800', display:'inline-flex', alignItems:'center', gap:'5px' }}><FaCheckDouble size={12}/> Finished Sequence</span>;
    if (logData.lastMessageStatus === 'read') return <span style={{ background: '#dbeafe', color: '#1d4ed8', padding: '6px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: '800', display:'inline-flex', alignItems:'center', gap:'5px' }}><FaCheckDouble size={12}/> Read</span>;
    if (logData.lastMessageStatus === 'delivered') return <span style={{ background: '#e2e8f0', color: '#475569', padding: '6px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: '800', display:'inline-flex', alignItems:'center', gap:'5px' }}><FaCheckDouble size={12}/> Delivered</span>;
    if (logData.lastMessageStatus === 'failed') return <span style={{ background: '#fee2e2', color: '#b91c1c', padding: '6px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: '800', display:'inline-flex', alignItems:'center', gap:'5px' }}><FaBan size={12}/> Failed</span>;
    return <span style={{ background: '#fef3c7', color: '#b45309', padding: '6px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: '800', display:'inline-flex', alignItems:'center', gap:'5px' }}><FaClock size={12}/> Waiting for next step</span>;
  };

  const nextDate = logData.nextFireAt?.toDate ? logData.nextFireAt.toDate().toLocaleString([], {month:'short', day:'numeric', hour:'2-digit', minute:'2-digit'}) : 'None';

  return (
    <tr style={{ borderBottom: '1px solid #e2e8f0', transition: 'background 0.2s', ':hover': {background: '#f8fafc'} }}>
      <td style={{ padding: '16px 25px', fontSize: '14px', fontWeight: '700', color: '#0f172a' }}>+{contactId}</td>
      <td style={{ padding: '16px 25px', fontSize: '13px', fontWeight: '800', color: THEME.primary }}>Step {logData.currentStep || 1}</td>
      <td style={{ padding: '16px 25px' }}>{getStatusBadge()}</td>
      <td style={{ padding: '16px 25px', fontSize: '12px', color: '#64748b', fontWeight: '600' }}>{logData.status === 'active' ? nextDate : '-'}</td>
      <td style={{ padding: '16px 25px' }}>
          {logData.status === 'active' && (
              <button onClick={() => onRemove(contactId)} style={{ background: '#fee2e2', color: '#b91c1c', border: 'none', padding: '6px 12px', borderRadius: '8px', fontSize: '11px', fontWeight: '800', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <FaStopCircle /> Halt Sequence
              </button>
          )}
      </td>
    </tr>
  );
};

// --- DRIP TRACKING MODAL ---
const DripTrackingModal = ({ campaign, user, onClose, isMobile }) => {
  const [enrollments, setEnrollments] = useState([]);
  const [stats, setStats] = useState({ enrolled: 0, sent: 0, read: 0, replied: 0, failed: 0 });

  useEffect(() => {
    const q = query(collection(db, "businesses", user.uid, "drip_enrollments"));
    const unsub = onSnapshot(q, (snap) => {
        const campaignData = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(d => d.campaignId === campaign.id);
        setEnrollments(campaignData);
        
        let enrolled = 0, sent = 0, read = 0, replied = 0, failed = 0;
        campaignData.forEach(e => {
            if (e.status !== 'un-enrolled') enrolled++;
            if (e.messagesSent) sent += e.messagesSent;
            if (e.lastMessageStatus === 'read') read++;
            if (e.replied) replied++;
            if (e.lastMessageStatus === 'failed') failed++;
        });
        setStats({ enrolled, sent, read, replied, failed });
    });
    return () => unsub();
  }, [campaign.id, user]);

  const handleHaltContact = async (contactId) => {
      if(window.confirm(`Stop sending automated messages to ${contactId}?`)) {
          await updateDoc(doc(db, "businesses", user.uid, "drip_enrollments", `${campaign.id}_${contactId}`), {
              status: 'un-enrolled',
              unEnrolledAt: serverTimestamp()
          });
      }
  };

  const StatBox = ({ title, value, icon, color, bg }) => (
    <div style={{ flex: 1, background: 'white', padding: '15px', borderRadius: '16px', border: `1px solid ${THEME.border}`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
      <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: bg, color: color, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '8px' }}>
        {icon}
      </div>
      <div style={{ fontSize: '24px', fontWeight: '800', color: THEME.textMain, lineHeight: '1' }}>{value}</div>
      <div style={{ fontSize: '11px', fontWeight: '700', color: THEME.textSub, textTransform: 'uppercase', marginTop: '5px', letterSpacing: '0.5px', textAlign: 'center' }}>{title}</div>
    </div>
  );

  return (
    <div style={{ background: 'white', width: '100%', maxWidth: '1000px', height: isMobile ? '100vh' : '85vh', borderRadius: isMobile ? '0' : '24px', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)' }}>
      <div style={{ padding: '25px 30px', borderBottom: `1px solid ${THEME.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'white', zIndex: 10 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '800', color: THEME.textMain, display: 'flex', alignItems: 'center', gap: '10px' }}>
            <FaChartPie color={THEME.primary} /> Flow Tracking Engine
          </h2>
          <p style={{ margin: '5px 0 0 0', color: THEME.textSub, fontSize: '14px', fontWeight: '500' }}>Live analytics for: <strong style={{color: THEME.primary}}>{campaign.name}</strong></p>
        </div>
        <button onClick={onClose} style={{ border: 'none', background: '#f1f5f9', width: '40px', height: '40px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: THEME.textSub, transition: 'background 0.2s' }}><FaTimes size={18} /></button>
      </div>

      <div style={{ padding: '20px 30px', background: '#f8fafc', borderBottom: `1px solid ${THEME.border}` }}>
        <div style={{ display: 'flex', gap: '15px', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
          <StatBox title="Active Enrolled" value={stats.enrolled} icon={<FaUsers size={16}/>} color="#4f46e5" bg="#e0e7ff" />
          <StatBox title="Total Sent" value={stats.sent} icon={<FaCheck size={14}/>} color="#64748b" bg="#f1f5f9" />
          <StatBox title="Read Rate" value={stats.read} icon={<FaCheckDouble size={14}/>} color="#1d4ed8" bg="#dbeafe" />
          <StatBox title="Replied (Lead)" value={stats.replied} icon={<FaReply size={14}/>} color="#15803d" bg="#dcfce7" />
          <StatBox title="Failed" value={stats.failed} icon={<FaBan size={14}/>} color="#b91c1c" bg="#fee2e2" />
        </div>
      </div>
      
      <div style={{ padding: '0', overflowY: 'auto', flex: 1, background: 'white' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ background: 'white', position: 'sticky', top: 0, zIndex: 10, boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
            <tr>
              <th style={{ padding: '15px 25px', textAlign: 'left', fontSize: '11px', fontWeight: '800', color: THEME.textSub, textTransform: 'uppercase', letterSpacing: '1px' }}>Contact Phone</th>
              <th style={{ padding: '15px 25px', textAlign: 'left', fontSize: '11px', fontWeight: '800', color: THEME.textSub, textTransform: 'uppercase', letterSpacing: '1px' }}>Current Step</th>
              <th style={{ padding: '15px 25px', textAlign: 'left', fontSize: '11px', fontWeight: '800', color: THEME.textSub, textTransform: 'uppercase', letterSpacing: '1px' }}>Sequence Status</th>
              <th style={{ padding: '15px 25px', textAlign: 'left', fontSize: '11px', fontWeight: '800', color: THEME.textSub, textTransform: 'uppercase', letterSpacing: '1px' }}>Next Action At</th>
              <th style={{ padding: '15px 25px', textAlign: 'left', fontSize: '11px', fontWeight: '800', color: THEME.textSub, textTransform: 'uppercase', letterSpacing: '1px' }}>Manual Control</th>
            </tr>
          </thead>
          <tbody>
            {enrollments.filter(e => e.status !== 'un-enrolled').length > 0 ? (
              enrollments.filter(e => e.status !== 'un-enrolled').map((item, idx) => (
                <DripTrackingRow key={idx} contactId={item.contactId} campaignId={campaign.id} user={user} onRemove={handleHaltContact} />
              ))
            ) : (
              <tr>
                <td colSpan="5" style={{ padding: '60px', textAlign: 'center', color: THEME.textSub, fontSize: '14px', fontWeight: '500' }}>
                  <FaChartBar size={40} style={{opacity: 0.2, marginBottom: '15px', display: 'block', margin: '0 auto'}}/>
                  No contacts are currently enrolled in this flow.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};


function DripCampaign() {
  const [user, setUser] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  const [campaigns, setCampaigns] = useState([]);
  const [searchQuery, setSearchQuery] = useState(""); // Pro Search Feature
  const [templates, setTemplates] = useState([]);
  const [allContacts, setAllContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [reportCampaign, setReportCampaign] = useState(null); 
  
  const [showBuilder, setShowBuilder] = useState(false);
  const [saving, setSaving] = useState(false);

  const [campName, setCampName] = useState("");
  const [triggerType, setTriggerType] = useState("NEW_LEAD"); 
  const [triggerTag, setTriggerTag] = useState(""); 
  const [triggerCustomDays, setTriggerCustomDays] = useState(10); // Custom Recurring Days
  const [steps, setSteps] = useState([
    { id: Date.now(), delayType: 'RELATIVE', delayValue: 1, delayUnit: 'DAYS', sendTime: '10:00', fixedDate: '', skipWeekends: false, templateId: '', templateName: '', templateData: null }
  ]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);

    const unsubscribeAuth = auth.onAuthStateChanged(async (u) => {
      setUser(u);
      if (u) {
        onSnapshot(query(collection(db, "businesses", u.uid, "drip_campaigns"), orderBy("createdAt", "desc")), (snap) => {
           setCampaigns(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });

        onSnapshot(query(collection(db, "businesses", u.uid, "templates")), (snap) => {
          setTemplates(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(t => t.status === "Approved"));
        });

        onSnapshot(query(collection(db, "businesses", u.uid, "contacts")), (snap) => {
          const contactsData = snap.docs.map(d => ({ id: d.id, ...d.data() }));
          setAllContacts(contactsData);
          updateAudiencePreview(triggerType, contactsData);
        });
      }
    });

    return () => {
      window.removeEventListener('resize', handleResize);
      unsubscribeAuth();
    };
  }, [triggerType]);

  // --- 🔥 AUDIENCE FILTERING LOGIC ---
  const updateAudiencePreview = (trigger, contactsList = allContacts) => {
    let filtered = [];
    const now = new Date();
    
    switch (trigger) {
      case "NEW_LEAD": 
        filtered = [...contactsList].sort((a, b) => {
             const timeA = a.lastActive?.toMillis ? a.lastActive.toMillis() : 0;
             const timeB = b.lastActive?.toMillis ? b.lastActive.toMillis() : 0;
             return timeB - timeA;
        }).slice(0, 10);
        break;
      case "INACTIVE_7_DAYS": 
        filtered = contactsList.filter(c => c.lastActive && (now - c.lastActive.toDate()) > 7 * 24 * 60 * 60 * 1000); 
        break;
      case "TAG_ADDED":
      case "MANUAL_ENROLLMENT": 
      case "MONTHLY_RECURRING": 
      case "CUSTOM_RECURRING": 
        filtered = contactsList.slice(0, 15); // Show sample size for preview
        break;
      default: 
        filtered = contactsList;
    }
    setFilteredContacts(filtered);
  };

  const handleTriggerChange = (e) => {
    const val = e.target.value;
    setTriggerType(val);
    updateAudiencePreview(val);
  };

  const addStep = () => {
    setSteps([...steps, { id: Date.now(), delayType: 'RELATIVE', delayValue: 1, delayUnit: 'DAYS', sendTime: '10:00', fixedDate: '', skipWeekends: false, templateId: '', templateName: '', templateData: null }]);
  };

  const removeStep = (id) => {
    if (steps.length === 1) return alert("You must have at least one step.");
    setSteps(steps.filter(s => s.id !== id));
  };

  const updateStep = (id, field, value) => {
    setSteps(steps.map(s => s.id === id ? { ...s, [field]: value } : s));
  };

  const handleTemplateSelect = (stepId, templateId) => {
    const selectedTpl = templates.find(t => t.id === templateId);
    setSteps(steps.map(s => s.id === stepId ? { ...s, templateId: templateId, templateName: selectedTpl ? selectedTpl.name : '', templateData: selectedTpl || null } : s));
  };

  const saveCampaign = async () => {
    if (!campName.trim()) return alert("Please name your campaign.");
    if (triggerType === 'TAG_ADDED' && !triggerTag.trim()) return alert("Please specify the tag for the trigger.");
    if (triggerType === 'CUSTOM_RECURRING' && (!triggerCustomDays || triggerCustomDays < 1)) return alert("Please specify a valid number of days for the custom recurring flow.");
    if (steps.some(s => !s.templateId)) return alert("⚠️ Every step MUST have an approved Meta Template selected.");
    if (steps.some(s => s.delayType === 'FIXED' && !s.fixedDate)) return alert("Please select a date and time for all fixed steps.");

    setSaving(true);
    try {
      await addDoc(collection(db, "businesses", user.uid, "drip_campaigns"), {
        name: campName,
        trigger: triggerType,
        triggerTag: triggerType === 'TAG_ADDED' ? triggerTag : null,
        triggerCustomDays: triggerType === 'CUSTOM_RECURRING' ? Number(triggerCustomDays) : null,
        steps: steps.map(s => ({
            delayType: s.delayType, delayValue: s.delayValue, delayUnit: s.delayUnit, sendTime: s.sendTime, fixedDate: s.fixedDate, skipWeekends: s.skipWeekends, templateId: s.templateId, templateName: s.templateName
        })),
        isActive: true,
        stats: { enrolled: 0, sent: 0, read: 0 },
        createdAt: serverTimestamp()
      });
      setShowBuilder(false);
      resetBuilder();
    } catch (e) { alert("Error saving: " + e.message); }
    setSaving(false);
  };

  const resetBuilder = () => {
    setCampName(""); setTriggerType("NEW_LEAD"); setTriggerTag(""); setTriggerCustomDays(10);
    setSteps([{ id: Date.now(), delayType: 'RELATIVE', delayValue: 1, delayUnit: 'DAYS', sendTime: '10:00', fixedDate: '', skipWeekends: false, templateId: '', templateName: '', templateData: null }]);
  };

  const toggleCampaignStatus = async (id, currentStatus) => {
    await updateDoc(doc(db, "businesses", user.uid, "drip_campaigns", id), { isActive: !currentStatus });
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name.substring(0, 2).toUpperCase();
  };

  const getRandomColor = (str) => {
    const colors = ['#ef4444', '#f97316', '#84cc16', '#0ea5e9', '#8b5cf6', '#d946ef', '#f43f5e'];
    let hash = 0;
    for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
  };

  const TemplatePreview = ({ tpl }) => {
    if (!tpl) return null;
    return (
        <div style={{ background: '#efeae2', borderRadius: '16px', overflow: 'hidden', border: `1px solid ${THEME.border}`, marginTop: '15px', display: 'flex', flexDirection: 'column', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
            <div style={{ background: '#008069', padding: '10px 15px', color: 'white', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '30px', height: '30px', borderRadius: '50%', background: 'white', color: '#008069', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 'bold' }}>Logo</div>
                <div style={{ fontSize: '14px', fontWeight: '600' }}>WhatsApp Preview</div>
            </div>
            <div style={{ padding: '20px', backgroundImage: 'url("https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png")', flex: 1 }}>
                <div style={{ background: 'white', padding: '12px', borderRadius: '0 12px 12px 12px', boxShadow: '0 1px 2px rgba(0,0,0,0.1)', maxWidth: '90%' }}>
                    {tpl.headerType === 'TEXT' && <div style={{ fontWeight: '800', fontSize: '15px', marginBottom: '8px', color: '#111b21' }}>{tpl.headerText}</div>}
                    {(tpl.headerType === 'IMAGE' || tpl.headerType === 'VIDEO') && (
                        <div style={{ height: '120px', background: '#e2e8f0', borderRadius: '8px', marginBottom: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: '12px', fontWeight: 'bold' }}>[ {tpl.headerType} MEDIA ]</div>
                    )}
                    <div style={{ fontSize: '14.5px', color: '#111b21', whiteSpace: 'pre-wrap', lineHeight: '1.4' }}>{tpl.body?.replace(/{{1}}/g, 'Customer')}</div>
                    {tpl.footer && <div style={{ fontSize: '11px', color: '#667781', marginTop: '8px' }}>{tpl.footer}</div>}
                    <div style={{ textAlign: 'right', fontSize: '10px', color: '#667781', marginTop: '4px' }}>10:45 AM</div>
                </div>
                {tpl.buttons && tpl.buttons.length > 0 && (
                    <div style={{ marginTop: '5px', maxWidth: '90%' }}>
                        {tpl.buttons.map((btn, i) => (
                            <div key={i} style={{ background: 'white', borderRadius: '8px', padding: '10px', marginTop: '4px', textAlign: 'center', color: '#00a884', fontWeight: '600', fontSize: '14px', boxShadow: '0 1px 1px rgba(0,0,0,0.1)' }}>{btn.text}</div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
  }

  const filteredCampaigns = campaigns.filter(c => c.name.toLowerCase().includes(searchQuery.toLowerCase()));

  if (!user) return <div style={{ padding: '40px', textAlign: 'center', fontWeight: 'bold' }}>Loading Pro Engine...</div>;

  return (
    <div className="drip-container" style={{ background: THEME.bg, minHeight: '100vh', padding: isMobile ? '15px' : '30px', fontFamily: '"Inter", sans-serif' }}>
      <style>{`
        .drip-container * { box-sizing: border-box; }
        .custom-scroll::-webkit-scrollbar { width: 6px; }
        .custom-scroll::-webkit-scrollbar-track { background: transparent; }
        .custom-scroll::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
        .node-card { transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); }
        .node-card:hover { transform: translateY(-4px); box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04); border-color: ${THEME.primaryLight}; }
        .canvas-bg { background-color: #f8fafc; background-image: radial-gradient(#cbd5e1 1.5px, transparent 1.5px); background-size: 24px 24px; }
        .flow-line { width: 4px; background: ${THEME.primaryLight}; margin: 0 auto; }
        /* Toggle Switch Styles */
        .toggle-switch { position: relative; display: inline-block; width: 44px; height: 24px; }
        .toggle-switch input { opacity: 0; width: 0; height: 0; }
        .slider { position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0; background-color: #cbd5e1; transition: .4s; border-radius: 24px; }
        .slider:before { position: absolute; content: ""; height: 18px; width: 18px; left: 3px; bottom: 3px; background-color: white; transition: .4s; border-radius: 50%; }
        input:checked + .slider { background-color: ${THEME.primary}; }
        input:checked + .slider:before { transform: translateX(20px); }
      `}</style>
      
      {/* --- DASHBOARD HEADER --- */}
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: '15px', marginBottom: '40px' }}>
        <div>
          <h1 style={{ fontSize: isMobile ? '26px' : '32px', fontWeight: '900', color: THEME.textMain, margin: 0, display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ background: `linear-gradient(135deg, ${THEME.primary}, #818cf8)`, color: 'white', padding: '10px', borderRadius: '14px', display: 'flex', boxShadow: '0 4px 10px rgba(79, 70, 229, 0.3)' }}><FaBullhorn size={24}/></div> Drip Campaigns
          </h1>
          <p style={{ color: THEME.textSub, margin: '8px 0 0 0', fontWeight: '500', fontSize: '15px' }}>Automate your WhatsApp follow-ups and nurture leads on autopilot.</p>
        </div>
        <button onClick={() => {setShowBuilder(true); updateAudiencePreview(triggerType);}} style={{ width: isMobile ? '100%' : 'auto', background: THEME.textMain, color: 'white', border: 'none', padding: '16px 28px', borderRadius: '14px', fontWeight: '800', fontSize: '15px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', boxShadow: '0 10px 20px -5px rgba(0,0,0,0.3)', transition: 'transform 0.2s', ':hover': {transform: 'translateY(-2px)'} }}>
          <FaRocket /> Build New Flow
        </button>
      </div>

      {/* --- CAMPAIGN LIST --- */}
      <div style={{ background: 'white', borderRadius: '24px', border: `1px solid ${THEME.border}`, overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)' }}>
        <div style={{ padding: '20px 30px', borderBottom: `1px solid ${THEME.border}`, background: '#f8fafc', display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'center', gap: '15px' }}>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: THEME.textMain }}>Running Automations</h3>
          
          {/* SEARCH BAR (PRO FEATURE) */}
          <div style={{ position: 'relative', width: isMobile ? '100%' : '300px' }}>
            <FaSearch style={{ position: 'absolute', top: '12px', left: '15px', color: THEME.textSub }} />
            <input 
              type="text" 
              placeholder="Search campaigns..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ width: '100%', padding: '10px 15px 10px 40px', borderRadius: '12px', border: `1px solid ${THEME.border}`, fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
            />
          </div>
        </div>
        
        {filteredCampaigns.length === 0 ? (
          <div style={{ padding: '80px 20px', textAlign: 'center', color: THEME.textSub }}>
            <FaRobot size={50} style={{ opacity: 0.15, marginBottom: '20px' }}/>
            <h3 style={{ fontSize: '20px', color: THEME.textMain, marginBottom: '10px' }}>No campaigns found</h3>
            <p style={{ fontSize: '15px' }}>{searchQuery ? "Try adjusting your search query." : "Click 'Build New Flow' to create your first sequence."}</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${THEME.border}`, background: 'white' }}>
                  <th style={{ textAlign: 'left', padding: '20px 30px', fontSize: '12px', color: THEME.textSub, textTransform: 'uppercase', fontWeight: '800' }}>Status</th>
                  <th style={{ textAlign: 'left', padding: '20px 30px', fontSize: '12px', color: THEME.textSub, textTransform: 'uppercase', fontWeight: '800' }}>Campaign Details</th>
                  <th style={{ textAlign: 'left', padding: '20px 30px', fontSize: '12px', color: THEME.textSub, textTransform: 'uppercase', fontWeight: '800' }}>Trigger Condition</th>
                  <th style={{ textAlign: 'left', padding: '20px 30px', fontSize: '12px', color: THEME.textSub, textTransform: 'uppercase', fontWeight: '800' }}>Sequence Length</th>
                  <th style={{ textAlign: 'left', padding: '20px 30px', fontSize: '12px', color: THEME.textSub, textTransform: 'uppercase', fontWeight: '800' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCampaigns.map(camp => (
                  <tr key={camp.id} style={{ borderBottom: `1px solid ${THEME.border}`, transition: 'background 0.2s', ':hover': {background: '#f8fafc'} }}>
                    <td style={{ padding: '20px 30px' }}>
                      <button onClick={() => toggleCampaignStatus(camp.id, camp.isActive)} style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: camp.isActive ? '#dcfce7' : '#f1f5f9', color: camp.isActive ? THEME.success : THEME.textSub, border: camp.isActive ? `1px solid #bbf7d0` : `1px solid ${THEME.border}`, padding: '8px 16px', borderRadius: '20px', fontSize: '12px', fontWeight: '800', cursor: 'pointer' }}>
                        {camp.isActive ? <><FaPlay size={10}/> ACTIVE</> : <><FaPause size={10}/> PAUSED</>}
                      </button>
                    </td>
                    <td style={{ padding: '20px 30px' }}>
                        <div style={{ fontWeight: '900', color: THEME.textMain, fontSize: '16px', marginBottom: '4px' }}>{camp.name}</div>
                    </td>
                    <td style={{ padding: '20px 30px' }}>
                        <span style={{ background: THEME.accent, color: THEME.textMain, padding: '6px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: '700', border: `1px solid ${THEME.border}`, display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                            {camp.trigger === 'CUSTOM_RECURRING' ? <FaRedoAlt size={10} color={THEME.primary} /> : null}
                            {camp.trigger.replace(/_/g, ' ')} 
                            {camp.triggerTag ? ` (${camp.triggerTag})` : ''}
                            {camp.trigger === 'CUSTOM_RECURRING' ? ` (${camp.triggerCustomDays} Days)` : ''}
                        </span>
                    </td>
                    <td style={{ padding: '20px 30px', fontWeight: '800', color: THEME.primary, fontSize: '15px' }}>{camp.steps.length} Steps</td>
                    <td style={{ padding: '20px 30px', display: 'flex', gap: '15px', alignItems: 'center' }}>
                      <button onClick={() => setReportCampaign(camp)} style={{ border: 'none', background: '#eff6ff', color: THEME.primary, padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontWeight: '800', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '5px', transition: 'all 0.2s', boxShadow: '0 2px 4px rgba(37,99,235,0.1)' }}>
                          <FaChartPie /> Track Flow
                      </button>
                      <button onClick={() => {if(window.confirm('Delete this flow forever?')) deleteDoc(doc(db, "businesses", user.uid, "drip_campaigns", camp.id))}} style={{ background: 'white', border: `1px solid ${THEME.border}`, color: THEME.error, cursor: 'pointer', padding: '10px', borderRadius: '10px' }}><FaTrash size={16} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* --- REAL-TIME TRACKING MODAL OVERLAY --- */}
      {reportCampaign && (
         <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.75)', backdropFilter: 'blur(8px)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: isMobile ? '0' : '20px' }}>
            <DripTrackingModal campaign={reportCampaign} user={user} onClose={() => setReportCampaign(null)} isMobile={isMobile} />
         </div>
      )}

      {/* --- FULLSCREEN BUILDER OVERLAY --- */}
      {showBuilder && (
        <div style={{ position: 'fixed', inset: 0, background: THEME.bg, zIndex: 1000, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          
          <div style={{ height: '75px', background: 'white', borderBottom: `1px solid ${THEME.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: isMobile ? '0 15px' : '0 30px', flexShrink: 0, boxShadow: '0 4px 10px rgba(0,0,0,0.05)', zIndex: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              <button onClick={() => {setShowBuilder(false); resetBuilder();}} style={{ background: THEME.bg, border: `1px solid ${THEME.border}`, width: '40px', height: '40px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: THEME.textMain }}><FaTimes size={16}/></button>
              <div>
                  <h2 style={{ margin: 0, fontSize: isMobile ? '18px' : '22px', fontWeight: '900', color: THEME.textMain }}>Flow Designer</h2>
                  <div style={{ fontSize: '13px', color: THEME.textSub, fontWeight: '600' }}>{campName || 'Untitled Campaign'}</div>
              </div>
            </div>
            <button onClick={saveCampaign} disabled={saving} style={{ background: `linear-gradient(135deg, ${THEME.primary}, #818cf8)`, color: 'white', border: 'none', padding: isMobile ? '10px 15px' : '12px 28px', borderRadius: '12px', fontWeight: '800', fontSize: isMobile ? '13px' : '15px', cursor: saving ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '8px', boxShadow: '0 4px 12px rgba(79,70,229,0.3)' }}>
              {saving ? 'Saving...' : <><FaCheckCircle size={16} /> <span style={{ display: isMobile ? 'none' : 'inline' }}>Save & Activate Flow</span><span style={{ display: isMobile ? 'inline' : 'none' }}>Save</span></>}
            </button>
          </div>

          <div style={{ display: 'flex', flex: 1, overflow: 'hidden', flexDirection: isMobile ? 'column' : 'row' }}>
            
            <div className="custom-scroll" style={{ width: isMobile ? '100%' : '400px', background: 'white', borderRight: isMobile ? 'none' : `1px solid ${THEME.border}`, borderBottom: isMobile ? `1px solid ${THEME.border}` : 'none', display: 'flex', flexDirection: 'column', flexShrink: 0, overflowY: 'auto', padding: isMobile ? '20px' : '30px' }}>
              
              <h3 style={{ margin: '0 0 25px 0', fontSize: '18px', fontWeight: '900', color: THEME.textMain, display: 'flex', alignItems: 'center', gap: '10px' }}><FaCogs color={THEME.primary}/> Campaign Settings</h3>
              
              <div style={{ marginBottom: '25px' }}>
                <label style={labelStyle}>Campaign Name</label>
                <input value={campName} onChange={e => setCampName(e.target.value)} placeholder="e.g. Lead Re-engagement" style={inputStyle} autoFocus/>
              </div>

              <div style={{ marginBottom: '25px' }}>
                <label style={labelStyle}>Entry Trigger (Who enters this flow?)</label>
                <select value={triggerType} onChange={handleTriggerChange} style={inputStyle}>
                  <option value="NEW_LEAD">Instantly when a New Lead is added</option>
                  <option value="TAG_ADDED">When a specific Tag is added</option>
                  <option value="INACTIVE_7_DAYS">When Lead is inactive for 7 Days</option>
                  <option value="MONTHLY_RECURRING">Monthly Recurring Flow (Auto-sends 30 Days)</option>
                  <option value="CUSTOM_RECURRING">Custom Recurring Schedule (Pro)</option>
                  <option value="MANUAL_ENROLLMENT">Manual Enrollment Only</option>
                </select>
              </div>

              {/* CUSTOM RECURRING INPUT (PRO FEATURE) */}
              {triggerType === 'CUSTOM_RECURRING' && (
                <div style={{ marginBottom: '25px', animation: 'fadeIn 0.3s' }}>
                  <label style={labelStyle}>Repeat Sequence Every</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <input 
                      type="number" 
                      min="1" 
                      value={triggerCustomDays} 
                      onChange={e => setTriggerCustomDays(e.target.value)} 
                      style={{ ...inputStyle, marginBottom: 0, width: '80px', textAlign: 'center', fontSize: '16px', fontWeight: 'bold' }} 
                    />
                    <span style={{ fontWeight: '800', color: THEME.textMain }}>Days</span>
                  </div>
                </div>
              )}

              {triggerType === 'TAG_ADDED' && (
                <div style={{ marginBottom: '25px', animation: 'fadeIn 0.3s' }}>
                  <label style={labelStyle}>Which Tag?</label>
                  <div style={{ position: 'relative' }}>
                    <FaTag style={{ position: 'absolute', top: '16px', left: '16px', color: THEME.textSub }} />
                    <input value={triggerTag} onChange={e => setTriggerTag(e.target.value)} placeholder="e.g. VIP_Customer" style={{...inputStyle, paddingLeft: '45px'}} />
                  </div>
                </div>
              )}

              {/* AUDIENCE PREVIEW SECTION */}
              <div style={{ background: 'white', borderRadius: '16px', border: `1px solid ${THEME.border}`, overflow: 'hidden', marginBottom: '30px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                  <div style={{ background: '#f8fafc', padding: '15px', borderBottom: `1px solid ${THEME.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '12px', fontWeight: '900', color: THEME.textMain, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '8px' }}><FaUsers color={THEME.primary}/> Audience Preview</span>
                      <span style={{ background: THEME.primaryLight, color: THEME.primary, padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '900' }}>{filteredContacts.length} Matches</span>
                  </div>
                  <div className="custom-scroll" style={{ padding: '15px', maxHeight: '250px', overflowY: 'auto' }}>
                      {filteredContacts.length === 0 ? (
                          <div style={{ textAlign: 'center', color: THEME.textSub, fontSize: '13px', padding: '20px 0' }}>No contacts match this trigger yet.</div>
                      ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                              {filteredContacts.map((contact, i) => {
                                const name = contact.clientName || contact.phone || 'Unknown';
                                const color = getRandomColor(name);
                                return (
                                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px', background: '#f8fafc', borderRadius: '12px', border: `1px solid ${THEME.border}` }}>
                                      <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: color, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 'bold' }}>
                                        {getInitials(name)}
                                      </div>
                                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        <span style={{ fontWeight: '800', color: THEME.textMain, fontSize: '13px' }}>{name}</span>
                                        <span style={{ color: THEME.textSub, fontSize: '11px' }}>
                                            {triggerType === 'NEW_LEAD' ? "Recent Lead Preview" : 
                                            (triggerType === 'MONTHLY_RECURRING' || triggerType === 'CUSTOM_RECURRING') ? "Recurring List Preview" : 
                                            "Ready to enroll"}
                                        </span>
                                      </div>
                                  </div>
                              )})}
                          </div>
                      )}
                  </div>
              </div>

              <div style={{ padding: '20px', background: '#eff6ff', borderRadius: '16px', border: `1px solid #bfdbfe` }}>
                <div style={{ fontSize: '12px', fontWeight: '900', color: '#1e40af', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>⚠️ META WHATSAPP RULE</div>
                <div style={{ fontSize: '13px', color: '#1e3a8a', lineHeight: '1.6', fontWeight: '500' }}>
                  Automated drips occur outside the standard 24-hour service window. You <strong>cannot type free-form text</strong>. You must select an Approved Template for each step.
                </div>
              </div>
            </div>

            <div className="canvas-bg custom-scroll" style={{ flex: 1, padding: isMobile ? '20px 10px' : '50px', overflowY: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              
              <div style={{ width: '100%', maxWidth: '850px', paddingBottom: '100px' }}>
                
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div style={{ background: '#0f172a', color: 'white', padding: '14px 28px', borderRadius: '24px', fontSize: '14px', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '1px', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.3)', border: '2px solid #334155', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {(triggerType === 'MONTHLY_RECURRING' || triggerType === 'CUSTOM_RECURRING') ? <FaSyncAlt color={THEME.success}/> : <FaBolt color={THEME.warning}/>} 
                    Start: {triggerType.replace(/_/g, ' ')}
                  </div>
                  <div className="flow-line" style={{ height: '40px' }}></div>
                </div>

                {steps.map((step, idx) => (
                  <div key={step.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div className="node-card" style={{ width: '100%', background: 'white', borderRadius: '24px', border: `2px solid ${THEME.border}`, padding: isMobile ? '20px' : '30px', position: 'relative' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px', borderBottom: `1px solid ${THEME.border}`, paddingBottom: '15px' }}>
                        <div style={{ fontSize: '18px', fontWeight: '900', color: THEME.textMain, display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{ background: THEME.primaryLight, color: THEME.primary, width: '32px', height: '32px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '15px' }}>{idx + 1}</div>
                            Action Node
                        </div>
                        <button onClick={() => removeStep(step.id)} style={{ color: THEME.error, background: '#fee2e2', border: 'none', padding: '8px 16px', borderRadius: '10px', fontSize: '13px', fontWeight: '800', cursor: 'pointer', transition: '0.2s', ':hover': {background: '#fecaca'} }}>Delete Step</button>
                      </div>

                      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? '25px' : '40px' }}>
                        
                        <div style={{ flex: 1 }}>
                          <label style={labelStyle}>Time Settings</label>
                          <div style={{ background: '#f8fafc', padding: '20px', borderRadius: '16px', border: `1px solid ${THEME.border}` }}>
                              
                              <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', background: 'white', padding: '6px', borderRadius: '12px', border: `1px solid ${THEME.border}` }}>
                                <button type="button" onClick={() => updateStep(step.id, 'delayType', 'RELATIVE')} style={step.delayType === 'RELATIVE' ? activeBtnStyle : inactiveBtnStyle}><FaClock/> Relative Delay</button>
                                <button type="button" onClick={() => updateStep(step.id, 'delayType', 'FIXED')} style={step.delayType === 'FIXED' ? activeBtnStyle : inactiveBtnStyle}><FaCalendarAlt/> Exact Date</button>
                              </div>

                              {step.delayType === 'RELATIVE' ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <span style={{ fontSize: '14px', color: THEME.textMain, fontWeight: '800' }}>Wait</span>
                                    <input type="number" min="0" value={step.delayValue} onChange={e => updateStep(step.id, 'delayValue', parseInt(e.target.value))} style={{...inputStyle, width: '80px', marginBottom: 0, textAlign: 'center', fontSize: '16px', fontWeight: 'bold'}} />
                                    <select value={step.delayUnit} onChange={e => updateStep(step.id, 'delayUnit', e.target.value)} style={{...inputStyle, flex: 1, marginBottom: 0, fontWeight: 'bold'}}>
                                      <option value="HOURS">Hours</option>
                                      <option value="DAYS">Days</option>
                                    </select>
                                  </div>
                                  
                                  <div style={{ background: 'white', padding: '15px', borderRadius: '12px', border: `1px solid ${THEME.border}` }}>
                                    <label style={{...labelStyle, fontSize: '10px', color: THEME.primary}}>Send exactly at (Time of Day)</label>
                                    <input type="time" value={step.sendTime} onChange={e => updateStep(step.id, 'sendTime', e.target.value)} style={{...inputStyle, marginBottom: 0, padding: '10px', fontWeight: 'bold'}} />
                                  </div>
                                </div>
                              ) : (
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                  <label style={{...labelStyle, fontSize: '10px'}}>Select Specific Date & Time</label>
                                  <input type="datetime-local" value={step.fixedDate} onChange={e => updateStep(step.id, 'fixedDate', e.target.value)} style={{...inputStyle, marginBottom: 0, padding: '16px', fontWeight: 'bold'}} />
                                </div>
                              )}

                              {/* SKIP WEEKENDS TOGGLE (PRO FEATURE) */}
                              <div style={{ marginTop: '20px', paddingTop: '15px', borderTop: `1px solid ${THEME.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <div>
                                  <div style={{ fontSize: '13px', fontWeight: '800', color: THEME.textMain }}>Skip Weekends</div>
                                  <div style={{ fontSize: '11px', color: THEME.textSub, marginTop: '2px' }}>Don't send on Sat & Sun</div>
                                </div>
                                <label className="toggle-switch">
                                  <input type="checkbox" checked={step.skipWeekends || false} onChange={e => updateStep(step.id, 'skipWeekends', e.target.checked)} />
                                  <span className="slider"></span>
                                </label>
                              </div>
                          </div>
                        </div>

                        <div style={{ flex: 1.5, display: 'flex', flexDirection: 'column' }}>
                          <label style={labelStyle}>Message Content</label>
                          <select value={step.templateId} onChange={e => handleTemplateSelect(step.id, e.target.value)} style={{...inputStyle, border: step.templateId ? `2px solid ${THEME.success}` : `2px dashed ${THEME.primary}`, background: step.templateId ? '#f0fdf4' : 'white', fontWeight: 'bold', padding: '16px'}}>
                            <option value="">-- Click to Select Approved Template --</option>
                            {templates.map(t => <option key={t.id} value={t.id}>✅ {t.name}</option>)}
                          </select>
                          {templates.length === 0 && <span style={{fontSize:'12px', color: THEME.error, marginTop: '5px', fontWeight: 'bold'}}>No approved templates found.</span>}
                          
                          {step.templateId && <TemplatePreview tpl={step.templateData} />}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flow-line" style={{ height: '40px' }}></div>
                  </div>
                ))}

                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <button onClick={addStep} style={{ background: 'white', border: `2px dashed ${THEME.primary}`, color: THEME.primary, padding: '15px 30px', borderRadius: '24px', fontSize: '16px', fontWeight: '900', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', transition: 'all 0.2s', boxShadow: '0 10px 15px -3px rgba(79,70,229,0.1)', ':hover': { transform: 'scale(1.05)' } }}>
                    <FaPlus size={16}/> Add Next Action Node
                  </button>
                </div>

              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

// Styles
const labelStyle = { display: 'block', fontSize: '11px', fontWeight: '900', textTransform: 'uppercase', color: THEME.textSub, marginBottom: '10px', letterSpacing: '0.5px' };
const inputStyle = { width: '100%', padding: '14px 16px', borderRadius: '12px', border: `1px solid ${THEME.border}`, fontSize: '14px', outline: 'none', background: 'white', boxSizing: 'border-box', marginBottom: '20px', fontFamily: '"Inter", sans-serif', color: THEME.textMain, transition: 'border 0.2s' };
const activeBtnStyle = { flex: 1, padding: '12px', border: `none`, background: THEME.primary, color: 'white', borderRadius: '8px', fontWeight: '800', fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer', boxShadow: '0 4px 6px rgba(79,70,229,0.2)' };
const inactiveBtnStyle = { flex: 1, padding: '12px', border: `none`, background: 'transparent', color: THEME.textSub, borderRadius: '8px', fontWeight: '800', fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer' };

export default DripCampaign;